import { environment } from '@env/environment';

import { Action, createReducer, on } from '@ngrx/store';
import {
  GetUser,
  GetUserFail,
  GetUserSuccess,
  InitUserStore,
  SetUserLoading,
  UpdateUser,
  UpdateUserOptions,
  UpdateUserExtras,
} from '../actions/user';
import { ModelState, Logout } from '@personal-area/global/state';
import { LoggedUser, ConfirmationMethod, User, UserOptions, Role } from '@personal-area/global/users/models';

export interface UserState extends ModelState {
  user: LoggedUser;
  options: UserOptions;
}

export const initialState: UserState = {
  options: {
    role: Role.Trader,
    location: environment.defaultLocation,
    hasOpenedAccounts: true,
  },
  user: {
    ...new User(),
    role: Role.Trader,
    country: environment.defaultLocation,
    isCabinetRole: false,
    isTradingPlatform: false,
    isPartner: false,
    isTrader: false,
    isExchanger: false,
    isInvestor: false,
    isLoaded: false,
    hasOpenedAccounts: true,
  },
  isLoading: false,
  isLoaded: false
};

// selectors
export const getUser = (state: UserState): LoggedUser => state.user;
export const isUserLoading = (state: UserState): boolean => state.isLoading;
export const isUserLoaded = (state: UserState): boolean => state.isLoaded;
export const getUserRole = (state: UserState): Role => state.user.role;
export const getUserCountry = (state: UserState): string => state.user.country;
export const getGeoLocation = (state: UserState): string => state.options.location;
// end of selectors

const _reducer = createReducer(
  initialState,
  on(InitUserStore, (state, { options, user }) => ({
    ...state,
    options: options || state.options,
    user: user || state.user,
  })),
  on(GetUser, state => {
    return {
      ...state,
      user: {
        ...state.user,
        isLoaded: false,
      },
      isLoaded: false
    };
  }),
  on(GetUserSuccess, (state, { user }) => {
    return {
      ...state,
      user: {
        ...user,
        role: state.options.role,
        isLoaded: user.id > 0,
        isCabinetRole: state.options.isCabinetRole,
        isPartner: state.options.isPartner,
        isTrader: state.options.isTrader,
        isTradingPlatform: state.options.isTradingPlatform,
        isInvestor: state.options.isInvestor,
        isExchanger: state.options.isExchanger,
        addressFormatted: `${user.billingAddress.zipcode}, ${user.billingAddress.city}, ${user.billingAddress.address}`,
        isAddressEmpty: user.billingAddress.zipcode === ''
          && user.billingAddress.city === ''
          && user.billingAddress.address === '',
        isConfirmationMethodEmail: user.confirmationMethod === ConfirmationMethod.Email,
        isConfirmationMethodPhone: user.confirmationMethod === ConfirmationMethod.Sms,
        hasOpenedAccounts: state.options.hasOpenedAccounts
      },
      isLoading: false,
      isLoaded: true
    };
  }),
  on(GetUserFail, state => {
    return {
      ...state,
      isLoading: false
    };
  }),
  on(SetUserLoading, state => {
    return {
      ...state,
      isLoading: true
    };
  }),
  on(UpdateUser, (state, { user }) => {
    return {
      ...state,
      user: {
        ...state.user,
        ...user
      }
    };
  }),
  on(UpdateUserOptions, (state, { opts }) => {
    const options = { ...state.options, ...opts };
    const {
      role,
      isCabinetRole,
      isTrader,
      isPartner,
      isExchanger,
      isTradingPlatform,
      hasOpenedAccounts,
    } = options;

    if (JSON.stringify(state.options) === JSON.stringify(options)) {
      return state;
    }

    const user = { ...state.user };

    if (role) {
      user.role = role;
    }

    user.isCabinetRole = !!isCabinetRole;
    user.isTrader = isTrader || user.role == Role.Trader;
    user.isPartner = isPartner || user.role == Role.Partner;
    user.isExchanger = isExchanger || user.role == Role.Exchanger;
    user.isTradingPlatform = isTradingPlatform || user.role == Role.TradingPlatform;
    user.hasOpenedAccounts = hasOpenedAccounts;

    return {
      ...state,
      user,
      options
    };
  }),
  on(UpdateUserExtras, (state, extras) => ({
    ...state,
    user: {
      ...state.user,
      extras: {
        ...state.user.extras,
        ...extras
      }
    }
  })),
  on(Logout, (state) => ({
    ...initialState,
    options: {
      ...initialState.options,
      location: state.options.location,
    }
  })),
);

export const userReducer = (state: UserState | undefined, action: Action) => _reducer(state, action);
